import React, { useEffect, useMemo, useState } from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useCart,
} from "../../components/CartContext";

import {
  fetchMenu,
} from "../../actions/menuActions";

import "./css/HomeScreen.css";


// ============================================================
// THEME 4 IMAGES
// ============================================================

const HERO_IMAGE =
  "https://neosparkexperienc.com/images/elegant-dark-restaurant-interior-with-dim-warm-lighting-and-.png";


const galleryImages = [
  {
    image:
      "https://www.mijnamstelveen.nl/runtime/images/21994/1280x698/IMG_6450.webp",
    title: "The First Impression",
  },

  {
    image:
      "https://unsplash.com/photos/ZIVhgpw_82M/download?force=true",
    title: "From The Grill",
  },

  {
    image:
      "https://unsplash.com/photos/RAUGKcReU8A/download?force=true",
    title: "Ocean & Fire",
  },

  {
    image:
      "https://unsplash.com/photos/MaWMfm-HCqQ/download?force=true",
    title: "The Craft",
  },

  {
    image:
      "https://axwwgrkdco.cloudimg.io/v7/__gmpics3___/e432a466f6204bd4872b53fab536d58d.jpg",
    title: "The Final Course",
  },

  {
    image: HERO_IMAGE,
    title: "An Intimate Escape",
  },
];


// ============================================================
// HELPERS
// ============================================================

function createSectionId(name) {
  return String(name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}


// ============================================================
// BACKEND IMAGE HELPER
// ============================================================

function getImageUrl(image) {
  if (!image) {
    return HERO_IMAGE;
  }

  if (
    image.startsWith("http://") ||
    image.startsWith("https://")
  ) {
    return image;
  }

  return `${image}`;
}


// ============================================================
// MENU CARD
// ============================================================

function MenuCard({
  item,
  onAdd,
}) {
  const number =
    item.display_order ||
    item.id ||
    "01";

  return (
    <article className="theme4-menu-card">

      <div className="theme4-menu-image-wrapper">

        <img
          src={getImageUrl(
            item.image ||
            item.image_url ||
            item.photo
          )}
          alt={item.name || "Menu item"}
          className="theme4-menu-image"
          loading="lazy"
        />

        <div className="theme4-menu-image-overlay">
        </div>

        {item.tag && (
          <span className="theme4-menu-tag">
            {item.tag}
          </span>
        )}

        <span className="theme4-menu-number">
          {String(number).padStart(2, "0")}
        </span>

      </div>


      <div className="theme4-menu-content">

        <div className="theme4-menu-title-row">

          <h3>
            {item.name}
          </h3>

          <span className="theme4-menu-price">
            ₹{item.price}
          </span>

        </div>


        <p className="theme4-menu-description">
          {item.description}
        </p>


        <button
          type="button"
          className="theme4-menu-add"
          onClick={() => onAdd(item)}
        >

          <span>
            ADD TO ORDER
          </span>

          <span>
            +
          </span>

        </button>

      </div>

    </article>
  );
}


// ============================================================
// FEATURED CARD
// ============================================================

function FeaturedCard({
  item,
  index,
  onAdd,
}) {
  return (
    <div
      className={
        index === 0
          ? "theme4-featured-card theme4-featured-large"
          : "theme4-featured-card"
      }
    >

      <div className="theme4-featured-image-wrapper">

        <img
          src={getImageUrl(
            item.image ||
            item.image_url ||
            item.photo
          )}
          alt={item.name}
          className="theme4-featured-image"
          loading="lazy"
        />

        <div className="theme4-featured-overlay">
        </div>

        <span className="theme4-featured-index">
          0{index + 1}
        </span>

      </div>


      <div className="theme4-featured-info">

        <span>
          {item.tag || "CHEF'S SELECTION"}
        </span>

        <h3>
          {item.name}
        </h3>

        <button
          type="button"
          onClick={() => onAdd(item)}
        >
          ADD TO ORDER →
        </button>

      </div>

    </div>
  );
}


// ============================================================
// HOME SCREEN
// ============================================================

function HomeScreen() {

  const {
    restaurantId,
    tableId,
  } = useParams();


  const navigate = useNavigate();

  const dispatch = useDispatch();


  // ==========================================================
  // RESTAURANT INFORMATION FROM BACKEND
  // ==========================================================

  const {
    restaurantName,
    mobile,
    email,
    address,
  } = useSelector(
    (state) => state.theme
  );


  // ==========================================================
  // MENU FROM REDUX
  // ==========================================================

  const {
    loading,
    categories = [],
    error,
  } = useSelector(
    (state) => state.menu
  );


  // ==========================================================
  // CART
  // ==========================================================

  const {
    addToCart,
    cartCount,
  } = useCart();


  // ==========================================================
  // ACTIVE CATEGORY
  // ==========================================================

  const [
    activeCategory,
    setActiveCategory,
  ] = useState(null);


  // ==========================================================
  // FETCH MENU
  // ==========================================================

  useEffect(() => {

    if (!restaurantId) {
      return;
    }

    dispatch(
      fetchMenu(restaurantId)
    );

  }, [
    dispatch,
    restaurantId,
  ]);


  // ==========================================================
  // NORMALIZE CATEGORIES
  // ==========================================================

  const normalizedCategories = useMemo(() => {

    if (!Array.isArray(categories)) {
      return [];
    }

    return [...categories]
      .filter(
        (category) =>
          category.is_active !== false
      )
      .sort(
        (a, b) =>
          Number(a.display_order || 0) -
          Number(b.display_order || 0)
      )
      .map(
        (category, index) => {

          const categoryId =
            category.id ||
            category.slug ||
            createSectionId(
              category.name
            ) ||
            `category-${index}`;

          const items =
            category.items ||
            category.menu_items ||
            category.menuItems ||
            [];

          return {
            ...category,

            id: String(categoryId),

            name:
              category.name ||
              `Category ${index + 1}`,

            shortName:
              category.short_name ||
              category.shortName ||
              category.name ||
              `CATEGORY ${index + 1}`,

            description:
              category.description ||
              "A carefully selected collection from our kitchen.",

            items: Array.isArray(items)
              ? [...items]
                  .filter(
                    (item) =>
                      item.is_available !== false
                  )
                  .sort(
                    (a, b) =>
                      Number(
                        a.display_order || 0
                      ) -
                      Number(
                        b.display_order || 0
                      )
                  )
              : [],
          };
        }
      );

  }, [
    categories,
  ]);


  // ==========================================================
  // SET FIRST CATEGORY
  // ==========================================================

  useEffect(() => {

    if (
      normalizedCategories.length === 0
    ) {
      return;
    }

    const exists =
      normalizedCategories.some(
        (category) =>
          category.id ===
          activeCategory
      );


    if (!activeCategory || !exists) {

      setActiveCategory(
        normalizedCategories[0].id
      );

    }

  }, [
    normalizedCategories,
    activeCategory,
  ]);


  // ==========================================================
  // CURRENT CATEGORY
  // ==========================================================

  const currentCategory =
    normalizedCategories.find(
      (category) =>
        category.id ===
        activeCategory
    ) ||
    normalizedCategories[0] ||
    null;


  // ==========================================================
  // FEATURED ITEMS
  // ==========================================================

  // First and second display-order items from EACH active category.
  const featuredItems = useMemo(() => normalizedCategories.flatMap(
    (category) => (category.items || []).slice(0, 2)
  ), [normalizedCategories]);

  // Slide one card at a time; three cards remain visible on mobile.
  const [featuredStart, setFeaturedStart] = useState(0);
  useEffect(() => {
    setFeaturedStart(0);
    if (featuredItems.length <= 3) return undefined;
    const timer = window.setInterval(() => {
      setFeaturedStart((index) => (index + 1) % featuredItems.length);
    }, 3500);
    return () => window.clearInterval(timer);
  }, [featuredItems.length]);
  const visibleFeatured = featuredItems.length <= 3
    ? featuredItems
    : Array.from({ length: 3 }, (_, offset) =>
        featuredItems[(featuredStart + offset) % featuredItems.length]
      );


  // ==========================================================
  // ADD TO CART
  // ==========================================================

  const handleAddToCart = (
    item
  ) => {

    addToCart({
      ...item,
      quantity: 1,
    });

  };


  // ==========================================================
  // OPEN CART
  // ==========================================================

  const handleCart = () => {

    navigate(
      `/${restaurantId}/table/${tableId}/cart`
    );

  };


  // ==========================================================
  // SCROLL
  // ==========================================================

  const scrollToSection = (
    sectionId
  ) => {

    const section =
      document.getElementById(
        sectionId
      );


    if (!section) {
      return;
    }


    section.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

  };


  // ==========================================================
  // SELECT CATEGORY
  // ==========================================================

  const handleCategory = (
    categoryId
  ) => {

    setActiveCategory(
      categoryId
    );


    setTimeout(() => {

      scrollToSection(
        "theme4-menu-items"
      );

    }, 50);

  };


  // ==========================================================
  // HOME
  // ==========================================================

  return (

    <div className="theme4-home">


      {/* =====================================================
          HERO
      ===================================================== */}

      <section
        id="home"
        className="theme4-hero"
      >

        <img
          src={HERO_IMAGE}
          alt={
            restaurantName
              ? `${restaurantName} restaurant`
              : "Luxury fine dining restaurant"
          }
          className="theme4-hero-image"
        />

        <div className="theme4-hero-overlay">
        </div>

        <div className="theme4-hero-grain">
        </div>


        <div className="theme4-hero-content">

          <span className="theme4-eyebrow">
            A CONTEMPORARY DINING EXPERIENCE
          </span>


          <h1>

            {restaurantName ||
              "Exceptional Dining"}

            <br />

            <em>
              Culinary Mastery
            </em>

          </h1>


          <p>

            An intimate expression of
            exceptional ingredients,
            timeless technique and
            modern gastronomy.

          </p>


          <div className="theme4-hero-actions">

            <button
              type="button"
              className="theme4-gold-button"
              onClick={() =>
                scrollToSection(
                  "menu"
                )
              }
            >

              EXPLORE MENU

              <span>
                →
              </span>

            </button>


            <button
              type="button"
              className="theme4-outline-button"
              onClick={() =>
                scrollToSection(
                  "story"
                )
              }
            >

              OUR STORY

            </button>

          </div>

        </div>


        <div className="theme4-hero-bottom">

          <span>
            SCROLL TO DISCOVER
          </span>

          <div className="theme4-scroll-line">
          </div>

        </div>

      </section>


      {/* =====================================================
          STORY
      ===================================================== */}

      <section
        id="story"
        className="theme4-intro"
      >

        <div className="theme4-section-container">

          <div className="theme4-intro-label">

            <span>
              01
            </span>

            <div>
            </div>

            <span>
              THE EXPERIENCE
            </span>

          </div>


          <div className="theme4-intro-grid">


            <div className="theme4-intro-title">

              <span className="theme4-small-gold">
                ESTABLISHED WITH PASSION
              </span>

              <h2>

                A Symphony

                <br />

                <em>
                  of Flavors
                </em>

              </h2>

            </div>


            <div className="theme4-intro-text">

              <p className="theme4-large-text">

                Where exceptional
                ingredients meet
                timeless technique.

              </p>


              <p>

                Every plate begins with
                carefully selected seasonal
                ingredients and ends with
                a quiet attention to detail.

                <br />
                <br />

                We believe luxury dining
                should feel effortless,
                personal and unforgettable.

              </p>


              <button
                type="button"
                className="theme4-text-link"
                onClick={() =>
                  scrollToSection(
                    "menu"
                  )
                }
              >

                DISCOVER OUR MENU

                <span>
                  →
                </span>

              </button>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          FEATURED
      ===================================================== */}

      <section
        id="ambiance"
        className="theme4-featured"
      >

        <div className="theme4-section-container">


          <div className="theme4-section-header">

            <div>

              <span className="theme4-small-gold">
                02 — SIGNATURE
              </span>

              <h2>

                Featured

                <em>
                  Creations
                </em>

              </h2>

            </div>


            <button
              type="button"
              className="theme4-text-link"
              onClick={() =>
                scrollToSection(
                  "menu"
                )
              }
            >

              VIEW MENU

              <span>
                →
              </span>

            </button>

          </div>


          {featuredItems.length > 0 ? (

            <div className="theme4-featured-grid theme4-featured-carousel">

              {visibleFeatured.map(
                (
                  item,
                  index
                ) => (

                  <FeaturedCard
                    key={
                      item.id ||
                      index
                    }
                    item={item}
                    index={index}
                    onAdd={
                      handleAddToCart
                    }
                  />

                )
              )}

            </div>

          ) : (

            <div className="theme4-menu-empty">

              <p>
                Our signature menu
                is being prepared.
              </p>

            </div>

          )}

        </div>

      </section>


      {/* =====================================================
          PHILOSOPHY
      ===================================================== */}

      <section
        className="theme4-philosophy"
      >

        <div className="theme4-philosophy-image">

          <img
            src={HERO_IMAGE}
            alt="Restaurant interior"
          />

        </div>


        <div className="theme4-philosophy-content">

          <span className="theme4-small-gold">
            OUR PHILOSOPHY
          </span>


          <h2>

            Simplicity.

            <br />

            <em>
              Refined.
            </em>

          </h2>


          <p>

            We let exceptional ingredients
            speak for themselves.

          </p>


          <div className="theme4-philosophy-stats">

            <div>

              <strong>
                01
              </strong>

              <span>
                QUALITY
              </span>

            </div>


            <div>

              <strong>
                02
              </strong>

              <span>
                CRAFT
              </span>

            </div>


            <div>

              <strong>
                03
              </strong>

              <span>
                EXPERIENCE
              </span>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          MENU
      ===================================================== */}

      <section
        id="menu"
        className="theme4-menu-section"
      >

        <div className="theme4-section-container">


          <div className="theme4-menu-heading">

            <span className="theme4-small-gold">
              03 — THE MENU
            </span>

            <h2>

              Discover

              <br />

              <em>
                Our Menu
              </em>

            </h2>

            <p>

              A collection of dishes
              created with precision,
              passion and the finest
              ingredients.

            </p>

          </div>


          {/* CATEGORY NAV */}

          {normalizedCategories.length > 0 && (

            <div className="theme4-category-nav">

              {normalizedCategories.map(
                (category) => (

                  <button
                    key={
                      category.id
                    }
                    type="button"
                    className={
                      activeCategory ===
                      category.id
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      handleCategory(
                        category.id
                      )
                    }
                  >

                    {category.shortName ||
                      category.name}

                  </button>

                )
              )}

            </div>

          )}


          {/* MENU ITEMS */}

          <div
            id="theme4-menu-items"
            className="theme4-menu-category"
          >

            {currentCategory && (

              <>

                <div className="theme4-menu-category-intro">

  <div>

    <span className="theme4-small-gold">
      {currentCategory.shortName &&
      currentCategory.shortName !== currentCategory.name
        ? currentCategory.shortName
        : "OUR MENU"}
    </span>

    <h3>
      {currentCategory.name}
    </h3>

  </div>

  {currentCategory.description && (
    <p>
      {currentCategory.description}
    </p>
  )}

</div>


                <div className="theme4-menu-grid">

                  {currentCategory.items &&
                  currentCategory.items.length > 0 ? (

                    currentCategory.items.map(
                      (item) => (

                        <MenuCard
                          key={
                            item.id
                          }
                          item={item}
                          onAdd={
                            handleAddToCart
                          }
                        />

                      )
                    )

                  ) : (

                    <div className="theme4-menu-empty">

                      <p>
                        No items available
                        in this category.
                      </p>

                    </div>

                  )}

                </div>

              </>

            )}


            {loading && (

              <div className="theme4-menu-loading">

                <p>
                  Loading menu...
                </p>

              </div>

            )}


            {!loading && error && (

              <div className="theme4-menu-error">

                <p>
                  Unable to load menu.
                </p>

              </div>

            )}

          </div>

        </div>

      </section>


      {/* =====================================================
          PRIVATE EVENTS
      ===================================================== */}

      <section
        id="events"
        className="theme4-private"
      >

        <div className="theme4-private-image">

          <img
            src={HERO_IMAGE}
            alt="Private dining"
          />

        </div>


        <div className="theme4-private-content">

          <span className="theme4-small-gold">
            PRIVATE EVENTS
          </span>


          <h2>

            Gather

            <br />

            <em>
              Beautifully.
            </em>

          </h2>


          <p>

            From intimate dinners to
            sophisticated celebrations,
            our private dining experience
            is designed around your occasion.

          </p>


          <div className="theme4-private-details">

            <div>

              <span>
                PRIVATE DINING
              </span>

              <strong>
                Intimate celebrations
              </strong>

            </div>


            <div>

              <span>
                CORPORATE EVENTS
              </span>

              <strong>
                Bespoke experiences
              </strong>

            </div>


            <div>

              <span>
                SPECIAL OCCASIONS
              </span>

              <strong>
                Tailored menus
              </strong>

            </div>

          </div>


          <button
            type="button"
            className="theme4-gold-button"
            onClick={() =>
              scrollToSection(
                "contact"
              )
            }
          >

            ENQUIRE WITH US

            <span>
              →
            </span>

          </button>

        </div>

      </section>


      {/* =====================================================
          GALLERY
      ===================================================== */}

      <section
        id="gallery"
        className="theme4-gallery"
      >

        <div className="theme4-section-container">


          <div className="theme4-section-header">

            <div>

              <span className="theme4-small-gold">
                04 — GALLERY
              </span>

              <h2>

                Inside

                <em>
                  Our World
                </em>

              </h2>

            </div>

          </div>


          <div className="theme4-gallery-grid">

            {galleryImages.map(
              (
                item,
                index
              ) => (

                <div
                  key={index}
                  className={
                    index === 0
                      ? "theme4-gallery-item theme4-gallery-large"
                      : "theme4-gallery-item"
                  }
                >

                  <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                  />

                  <div className="theme4-gallery-overlay">

                    <span>
                      0{index + 1}
                    </span>

                    <strong>
                      {item.title}
                    </strong>

                  </div>

                </div>

              )
            )}

          </div>

        </div>

      </section>


      {/* =====================================================
          VISIT
      ===================================================== */}

      <section
        id="visit"
        className="theme4-visit"
      >

        <div className="theme4-visit-content">

          <span className="theme4-small-gold">
            VISIT US
          </span>


          <h2>

            Your table

            <br />

            <em>
              awaits.
            </em>

          </h2>


          {/* BACKEND ADDRESS */}

          <p>

            {address ||
              "Restaurant address"}

          </p>


          {/* BACKEND PHONE */}

          {mobile && (

            <p>

              <strong>
                PHONE
              </strong>

              <br />

              <a
                href={`tel:${mobile}`}
                style={{
                  color:
                    "inherit",
                  textDecoration:
                    "none",
                }}
              >
                {mobile}
              </a>

            </p>

          )}


          {/* BACKEND EMAIL */}

          {email && (

            <p>

              <strong>
                EMAIL
              </strong>

              <br />

              <a
                href={`mailto:${email}`}
                style={{
                  color:
                    "inherit",
                  textDecoration:
                    "none",
                }}
              >
                {email}
              </a>

            </p>

          )}


          <div className="theme4-opening-hours">

            <div>

              <span>
                RESTAURANT
              </span>

              <strong>
                Please contact us
                for opening hours
              </strong>

            </div>

          </div>


          <button
            type="button"
            className="theme4-gold-button"
            onClick={() => {

              if (mobile) {

                window.location.href =
                  `tel:${mobile}`;

              }

            }}
          >

            CONTACT US

            <span>
              →
            </span>

          </button>

        </div>


        <div className="theme4-visit-map">

          <div className="theme4-map-grid">

            <div className="theme4-map-road road-one">
            </div>

            <div className="theme4-map-road road-two">
            </div>

            <div className="theme4-map-road road-three">
            </div>

            <div className="theme4-map-road road-four">
            </div>

          </div>


          <div className="theme4-map-marker">

            <span>
              ✦
            </span>

            <small>
              {restaurantName ||
                "RESTAURANT"}
            </small>

          </div>

        </div>

      </section>


      {/* =====================================================
          FINAL CTA
      ===================================================== */}

      <section className="theme4-final">

        <img
          src={HERO_IMAGE}
          alt="Luxury restaurant"
          className="theme4-final-image"
        />

        <div className="theme4-final-overlay">
        </div>


        <div className="theme4-final-content">

          <span className="theme4-small-gold">

            {restaurantName ||
              "FINE DINING"}

          </span>


          <h2>

            An Intimate

            <br />

            <em>
              Escape
            </em>

          </h2>


          <p>

            Designed to take you away
            from the outside world.

          </p>


          <button
            type="button"
            className="theme4-gold-button"
            onClick={() =>
              scrollToSection(
                "menu"
              )
            }
          >

            EXPLORE MENU

            <span>
              →
            </span>

          </button>

        </div>

      </section>


      {/* =====================================================
          FLOATING CART
      ===================================================== */}

      {cartCount > 0 && (

        <button
          type="button"
          className="theme4-floating-cart"
          onClick={
            handleCart
          }
        >

          <span className="theme4-floating-cart-icon">
            🛒
          </span>

          <span>
            VIEW ORDER
          </span>

          <strong>
            {cartCount}
          </strong>

        </button>

      )}

    </div>

  );
}


export default HomeScreen;